import React, { useState, useEffect, useCallback } from 'react';
import { Search, Users, Phone, Mail } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { API } from '@/utils/api';
import axios from 'axios';
import AtelierLayout from '@/components/atelier/AtelierLayout';

const AtelierClients = ({ onLogout }) => {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients`);
      setClients(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = clients.filter(c => {
    const q = search.toLowerCase();
    if (!q) return true;
    return `${c.first_name} ${c.last_name}`.toLowerCase().includes(q)
      || (c.email || '').toLowerCase().includes(q)
      || (c.phone || '').includes(q);
  });

  return (
    <AtelierLayout onLogout={onLogout}>
      <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-4" data-testid="atelier-clients-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-violet-600" /> Clients
          </h1>
          <p className="text-sm text-slate-500">{filtered.length} client{filtered.length > 1 ? 's' : ''}</p>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher nom, email, telephone..." className="pl-9" data-testid="clients-search" />
        </div>

        {loading ? (
          <p className="text-center text-slate-400 py-10">Chargement...</p>
        ) : filtered.length === 0 ? (
          <p className="text-center text-slate-400 py-10">Aucun client trouve</p>
        ) : (
          <div className="grid gap-2">
            {filtered.map(c => (
              <Card key={c.id} className="p-4 hover:bg-slate-50/50 transition-colors" data-testid={`client-${c.id}`}>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center shrink-0">
                    <span className="text-sm font-bold text-violet-700">
                      {(c.first_name || '')[0]}{(c.last_name || '')[0]}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 text-sm">{c.first_name} {c.last_name}</p>
                    <div className="flex items-center gap-4 text-xs text-slate-500 flex-wrap mt-0.5">
                      {c.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {c.phone}
                        </span>
                      )}
                      {c.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {c.email}
                        </span>
                      )}
                    </div>
                  </div>
                  {c.address && (
                    <p className="text-xs text-slate-400 hidden md:block max-w-[200px] truncate">{c.address}</p>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AtelierLayout>
  );
};

export default AtelierClients;
